import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { CheckCircle2, Info, Pause, Play, RotateCcw, Video } from 'lucide-react'
import { extractYouTubeId } from '@quebon/shared-learning'

interface YouTubeStateEvent {
  data: number
}

interface YouTubeApi {
  Player: new (
    element: HTMLIFrameElement,
    options: {
      events?: {
        onReady?: () => void
        onStateChange?: (event: YouTubeStateEvent) => void
      }
      playerVars?: Record<string, number | string>
    },
  ) => YouTubePlayer
  PlayerState: { ENDED: number; PLAYING: number; PAUSED: number }
}

interface YouTubePlayer {
  playVideo?: () => void
  pauseVideo?: () => void
  getCurrentTime?: () => number
  getDuration?: () => number
  seekTo?: (seconds: number, allowSeekAhead?: boolean) => void
  destroy?: () => void
}

export interface SolutionVideoPlayerHandle {
  pause: () => void
}

interface WatchStatus {
  watched: boolean
  watchedLabel: string
  autoLabel: string
}

interface Props {
  videoUrl: string
  title: string
  onEnded?: () => void
  playLabel: string
  pauseLabel: string
  progressLabel: string
  rewindLabel: string
  seekPolicyLabel: string
  restrictSeeking: boolean
  watchStatus?: WatchStatus
}

declare global {
  interface Window {
    YT?: YouTubeApi
    onYouTubeIframeAPIReady?: () => void
  }
}

let youtubeApiPromise: Promise<YouTubeApi> | null = null
const COMPLETION_OFFSET_SECONDS = 2

function loadYouTubeApi(): Promise<YouTubeApi> {
  if (window.YT) return Promise.resolve(window.YT)
  if (youtubeApiPromise) return youtubeApiPromise

  youtubeApiPromise = new Promise((resolve, reject) => {
    const previousReady = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      previousReady?.()
      if (window.YT) resolve(window.YT)
      else reject(new Error('YouTube iframe API did not initialize'))
    }

    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const script = document.createElement('script')
      script.src = 'https://www.youtube.com/iframe_api'
      script.async = true
      script.onerror = () => reject(new Error('Failed to load YouTube iframe API'))
      document.head.appendChild(script)
    }
  })

  return youtubeApiPromise
}

function withYouTubeJsApi(videoUrl: string): string {
  const url = new URL(videoUrl)
  url.searchParams.set('enablejsapi', '1')
  url.searchParams.set('origin', window.location.origin)
  url.searchParams.set('controls', '0')
  url.searchParams.set('disablekb', '1')
  url.searchParams.set('rel', '0')
  url.searchParams.set('iv_load_policy', '3')
  url.searchParams.set('modestbranding', '0')
  url.searchParams.set('playsinline', '1')
  url.searchParams.set('fs', '0')
  return url.toString()
}

function formatVideoTime(seconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(safeSeconds / 60)
  const rest = safeSeconds % 60
  return `${minutes}:${String(rest).padStart(2, '0')}`
}

function getYouTubePosterUrls(videoUrl: string): { primary: string; fallback: string } | null {
  const videoId = extractYouTubeId(videoUrl)
  if (!videoId) return null
  return {
    primary: `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
    fallback: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
  }
}

/** 학습자 리뷰와 LMS 미리보기가 함께 쓰는 단원평가 손풀이 플레이어. */
export const SolutionVideoPlayer = forwardRef<SolutionVideoPlayerHandle, Props>(function SolutionVideoPlayer({
  videoUrl,
  title,
  onEnded,
  playLabel,
  pauseLabel,
  progressLabel,
  rewindLabel,
  seekPolicyLabel,
  restrictSeeking,
  watchStatus,
}, ref) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const playerRef = useRef<YouTubePlayer | null>(null)
  const [ready, setReady] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [showPoster, setShowPoster] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const furthestWatchedTimeRef = useRef(0)
  const completionReportedRef = useRef(watchStatus?.watched ?? false)
  const playingRef = useRef(false)
  const onEndedRef = useRef(onEnded)

  useEffect(() => {
    onEndedRef.current = onEnded
  }, [onEnded])

  useEffect(() => {
    completionReportedRef.current = watchStatus?.watched ?? false
  }, [watchStatus?.watched])

  const reportCompletion = useCallback(() => {
    if (completionReportedRef.current) return
    completionReportedRef.current = true
    onEndedRef.current?.()
  }, [])

  const updateProgress = useCallback(() => {
    const player = playerRef.current
    const nextCurrentTime = player?.getCurrentTime?.() ?? 0
    const nextDuration = player?.getDuration?.() ?? 0
    furthestWatchedTimeRef.current = Math.max(furthestWatchedTimeRef.current, nextCurrentTime)
    setCurrentTime(nextCurrentTime)
    setDuration(nextDuration)

    if (
      nextDuration > COMPLETION_OFFSET_SECONDS &&
      nextCurrentTime >= nextDuration - COMPLETION_OFFSET_SECONDS
    ) {
      reportCompletion()
    }
  }, [reportCompletion])

  const pause = useCallback(() => {
    playerRef.current?.pauseVideo?.()
    playingRef.current = false
    setPlaying(false)
    updateProgress()
  }, [updateProgress])

  useImperativeHandle(ref, () => ({ pause }), [pause])

  useEffect(() => {
    let disposed = false

    void loadYouTubeApi().then((yt) => {
      const iframe = iframeRef.current
      if (disposed || !iframe) return
      playerRef.current = new yt.Player(iframe, {
        playerVars: {
          autoplay: 0,
          controls: 0,
          showinfo: 0,
          rel: 0,
          iv_load_policy: 3,
          modestbranding: 0,
          playsinline: 1,
          enablejsapi: 1,
          fs: 0,
          color: 'white',
          disablekb: 1,
        },
        events: {
          onReady: () => {
            if (!disposed) {
              setReady(true)
              updateProgress()
            }
          },
          onStateChange: (event) => {
            if (event.data === yt.PlayerState.PLAYING) {
              playingRef.current = true
              setPlaying(true)
              setShowPoster(false)
              updateProgress()
            }
            if (event.data === yt.PlayerState.PAUSED) {
              playingRef.current = false
              setPlaying(false)
              updateProgress()
            }
            if (event.data === yt.PlayerState.ENDED) {
              playingRef.current = false
              setPlaying(false)
              setShowPoster(true)
              updateProgress()
              reportCompletion()
            }
          },
        },
      })
    })

    return () => {
      disposed = true
      playerRef.current?.destroy?.()
      playerRef.current = null
    }
  }, [reportCompletion, updateProgress])

  useEffect(() => {
    if (!playing) return undefined
    updateProgress()
    const intervalId = window.setInterval(updateProgress, 500)
    return () => window.clearInterval(intervalId)
  }, [playing, updateProgress])

  const togglePlayback = () => {
    if (playing) {
      pause()
      return
    }
    playerRef.current?.playVideo?.()
    playingRef.current = true
    setPlaying(true)
    updateProgress()
  }

  const progressPercent = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0
  const watchedPercent = duration > 0
    ? Math.min(100, Math.max(0, (furthestWatchedTimeRef.current / duration) * 100))
    : 0

  const seek = (nextTime: number) => {
    if (restrictSeeking && nextTime > furthestWatchedTimeRef.current) return
    const player = playerRef.current
    const shouldResume = playingRef.current
    player?.seekTo?.(nextTime, true)
    setCurrentTime(nextTime)
    if (shouldResume) {
      player?.playVideo?.()
      playingRef.current = true
      setPlaying(true)
    }
  }

  const rewind = () => {
    seek(Math.max(0, currentTime - 10))
  }

  const posterUrls = getYouTubePosterUrls(videoUrl)

  return (
    <div className="flex flex-col gap-3">
      <div className="group relative aspect-video w-full overflow-hidden rounded-2xl bg-black shadow-[0_14px_32px_rgba(19,19,19,0.18)] ring-1 ring-black/10">
        <iframe
          ref={iframeRef}
          className="pointer-events-none absolute left-0 top-[-200px] h-[calc(100%+400px)] w-full"
          src={withYouTubeJsApi(videoUrl)}
          title={title}
          allow="autoplay; encrypted-media"
        />
        {showPoster && posterUrls ? (
          <div data-testid="solution-video-poster" className="absolute inset-0 bg-black" aria-hidden="true">
            <img
              src={posterUrls.primary}
              alt=""
              className="h-full w-full object-cover"
              draggable={false}
              onError={(event) => {
                event.currentTarget.onerror = null
                event.currentTarget.src = posterUrls.fallback
              }}
            />
          </div>
        ) : null}
        <div className="absolute inset-0 flex items-center justify-center bg-transparent" aria-hidden="true" />
        <div className="absolute inset-x-0 top-0 flex items-center justify-between bg-linear-to-b from-black/70 to-transparent px-4 pb-8 pt-4">
          <span className="inline-flex items-center gap-2 rounded-full bg-black/70 px-3 py-1.5 text-[13px] font-bold text-white">
            <Video className="h-4 w-4" aria-hidden />
            {title}
          </span>
          <span className="rounded-full bg-black/55 px-3 py-1.5 text-[12px] font-semibold text-white/90">
            {seekPolicyLabel}
          </span>
        </div>
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <button
            type="button"
            onClick={togglePlayback}
            disabled={!ready}
            aria-hidden="true"
            tabIndex={-1}
            className={`inline-flex h-16 w-16 items-center justify-center rounded-full bg-white/95 text-lc-gray-900 shadow-[0_8px_24px_rgba(0,0,0,0.3)] transition hover:scale-105 hover:bg-white active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 ${
              playing
                ? 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100'
                : 'opacity-100'
            }`}
          >
            {playing ? <Pause className="h-7 w-7" /> : <Play className="ml-1 h-7 w-7 fill-current" />}
            <span className="sr-only">{playing ? pauseLabel : playLabel}</span>
          </button>
        </div>
        <div className="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/95 via-black/75 to-transparent px-4 pb-4 pt-12 sm:px-5">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={rewind}
              disabled={!ready || currentTime <= 0}
              aria-label={rewindLabel}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-black/55 text-white transition hover:bg-black/70 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <RotateCcw className="h-5 w-5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={togglePlayback}
              disabled={!ready}
              aria-label={playing ? pauseLabel : playLabel}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-lc-gray-900 shadow-md transition hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {playing ? <Pause className="h-5 w-5" aria-hidden /> : <Play className="ml-0.5 h-5 w-5 fill-current" aria-hidden />}
            </button>
            <div className="min-w-0 flex-1">
              <div className="relative h-6 w-full">
                <div
                  role="progressbar"
                  aria-label={progressLabel}
                  aria-valuemin={0}
                  aria-valuemax={Math.round(duration)}
                  aria-valuenow={Math.round(currentTime)}
                  className="absolute inset-x-0 top-2 h-2 rounded-full bg-white/25"
                >
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-white/35"
                    style={{ width: `${watchedPercent}%` }}
                  />
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-lc-lightblue-300 transition-[width] duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                  <span
                    data-testid="video-progress-handle"
                    aria-hidden="true"
                    className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-lc-lightblue-300 shadow-[0_2px_6px_rgba(0,0,0,0.35)] transition-[left] duration-100"
                    style={{ left: `${progressPercent}%` }}
                  />
                </div>
                <input
                  type="range"
                  min={0}
                  max={Math.max(0, duration)}
                  step={0.1}
                  value={Math.min(currentTime, duration || currentTime)}
                  onChange={(event) => seek(Number(event.target.value))}
                  aria-label={progressLabel}
                  className="absolute inset-0 h-6 w-full cursor-pointer opacity-0"
                />
              </div>
              <div className="mt-0.5 flex items-center justify-between gap-3 text-[12px] font-semibold tabular-nums text-white/90">
                <span>{formatVideoTime(currentTime)}</span>
                <span>{formatVideoTime(duration)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {watchStatus ? (
        <div
          data-testid="solution-video-status"
          className={`flex items-center gap-2 rounded-[10px] px-2.5 py-2 ${
            watchStatus.watched
              ? 'bg-lc-key-500 text-lc-text-on-key'
              : 'border border-lc-v3-gray-300 bg-white text-lc-text-secondary'
          }`}
        >
          {watchStatus.watched ? (
            <CheckCircle2 className="size-5 shrink-0" aria-hidden />
          ) : (
            <Info className="size-5 shrink-0" aria-hidden />
          )}
          <p className="text-lc-v3-body-2 font-semibold">
            {watchStatus.watched ? watchStatus.watchedLabel : watchStatus.autoLabel}
          </p>
        </div>
      ) : null}
    </div>
  )
})
